import React, { useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import { withTheme } from 'styled-components';
import PropTypes from 'prop-types';
import { setToken } from 'shared-components/src/utils/auth';
import { getPayAccountUrl } from 'shared-components/src/utils/whitelabel';
import ReCAPTCHA from '../../../../components/ReCaptcha';
import { isCAMSDefaultHost } from '../../../../utils/cams';
import { withLang } from '../../../../utils/locale';
import { errorMessage } from '../../../../utils/validationHandlers';
import { RecaptchaWrapper } from '../../styles';
import {
  PageWrapper,
  BlobShape,
  GeometricShapes,
  Shape,
  CamsHeader,
  LogoContainer,
  HeaderNav,
  HeaderLink,
  LanguageToggle,
  MainContent,
  Wrapper,
  Title,
  Subtitle,
  FormContainer,
  InputLabel,
  Input,
  InputWrapper,
  InputRow,
  InputWithTooltip,
  TooltipContainer,
  HelpIconButton,
  TooltipBubble,
  Button,
  ErrorMessage,
  SuccessMessage,
  FieldErrorMessage,
  LoginLink,
  AccessCodeLink,
} from './styles';
import CamsPageFooter from './CamsPageFooter';

// BIN configuration for CAMS login.
// Only cards with these BINs continue through the CAMS activation flow;
// any other BIN falls through to the external portal redirect.
// Configured via REACT_APP_CAMS_ALLOWED_BINS as a comma-separated list of 6-8 digit BIN prefixes.
// Falls back to a built-in default list when the env var is not set.
const parseAllowedBins = () => {
  const fromEnv = (process.env.REACT_APP_CAMS_ALLOWED_BINS || '')
    .split(',')
    .map(bin => bin.trim())
    .filter(Boolean);
  return fromEnv.length > 0 ? fromEnv : ['453740', '453745'];
};
const CAMS_ALLOWED_BINS = parseAllowedBins();
// URL to redirect non-allowed BINs to
const CAMS_REDIRECT_URL = 'https://mycardholdersupport.com/login';

// Step 1: Card number entry
// Step 2: CVV and access code entry
const STEP_CARD_NUMBER = 1;
const STEP_CREDENTIALS = 2;

const CamsLogin = ({ theme, locale, setLocale }) => {
  const intl = useIntl();
  const page = 'camslogin';

  const [step, setStep] = useState(STEP_CARD_NUMBER);
  const [cardNumber, setCardNumber] = useState('');
  const [cvv, setCvv] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [redirectMessage, setRedirectMessage] = useState(null);

  const recaptchaRef = useRef(null);

  // Captcha handlers
  const handleCaptchaChange = token => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken(null);
  };

  const handleCaptchaError = () => {
    setCaptchaToken(null);
    setError('errors-recaptcha_timeout');
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const currentLocale = locale || 'en';
  const toggleLocale = currentLocale === 'en' ? 'fr' : 'en';

  const handleLocaleChange = () => {
    if (setLocale) {
      sessionStorage.setItem('locale', toggleLocale);
      setLocale(toggleLocale);
    }
  };

  // Check if card BIN matches any in the given list
  const matchesBin = (card, binList) => binList.some(bin => card.startsWith(bin));

  const validateCardNumber = () => {
    const errors = {};
    if (!cardNumber || cardNumber.length !== 16) {
      errors.cardNumber = `${page}-cardnumber-errormsg`;
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateCredentials = () => {
    const errors = {};
    if (!cvv || cvv.length < 3 || cvv.length > 4) {
      errors.cvv = `${page}-cvv-errormsg`;
    }
    if (!accessCode || accessCode.length < 1) {
      errors.accessCode = `${page}-accesscode-errormsg`;
    }
    if (!captchaToken) {
      errors.captcha = 'errors-recaptcha_required';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getErrorMessage = (errorCode) => {
    // Map backend error codes to i18n message keys
    const errorMap = {
      'invalid_card': `${page}-invalid-card-error`,
      'invalid_cvv': `${page}-invalid-cvv-error`,
      'invalid_access_code': `${page}-invalid-accesscode-error`,
      'cams_login_disabled': `${page}-cams-disabled-error`,
      'already_registered': `${page}-already-registered-error`,
      'cams-login-failed-error': `${page}-invalid-credentials-error`,
      'captcha_validation_failed': 'errors-captcha_validation_failed',
      'captcha_response_missing': 'errors-recaptcha_required',
      'captcha_validation_error': 'errors-captcha_validation_failed',
    };
    return intl.messages[errorMap[errorCode]] ? errorMap[errorCode] : `${page}-invalid-credentials-error`;
  };

  // Step 1: Validate card number and check BIN
  const handleCardNumberSubmit = e => {
    e.preventDefault();
    setError(null);

    if (!validateCardNumber()) {
      return;
    }

    // Allowlist-based routing: only the supported BINs continue to step 2.
    // Any other BIN is redirected to the external portal.
    if (matchesBin(cardNumber, CAMS_ALLOWED_BINS)) {
      setStep(STEP_CREDENTIALS);
      return;
    }

    window.location.href = CAMS_REDIRECT_URL;
  };

  // Step 2: Submit credentials for login
  const handleCredentialsSubmit = async e => {
    e.preventDefault();
    setError(null);

    if (!validateCredentials()) {
      return;
    }

    setLoading(true);

    try {
      const res = await fetch('/api/v1/cardholder_portal/cams_login', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ card_number: cardNumber, cvv, access_code: accessCode, recaptcha_token: captchaToken }),
      });

      const body = await res.json();

      if (!res.ok) {
        if (body.error && body.error.code) {
          throw new Error(body.error.code);
        }
        throw new Error('cams-login-failed-error');
      }

      // Store the token
      setToken(body.token, body.exp);

      // Store email for pre-populating registration form
      if (body.email) {
        sessionStorage.setItem('register_email', body.email);
      }

      // Clear any stale MFA session data
      sessionStorage.removeItem('mfa_user_sub');
      sessionStorage.removeItem('mfa_setup_data');

      // Redirect to CAMS registration page
      window.location.pathname = '/cams-register';
    } catch (err) {
      setLoading(false);
      // Reset captcha on error
      setCaptchaToken(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      // If user is already registered, show message and redirect to payaccount login
      // Must use payaccount.io domain since cookies are domain-scoped
      if (err.message === 'already_registered') {
        setRedirectMessage(intl.messages[`${page}-already-registered-error`]);
        setTimeout(() => {
          window.location.href = withLang(`${getPayAccountUrl()}/login`, currentLocale);
        }, 3000);
        return;
      }
      setError(getErrorMessage(err.message));
    }
  };

  const handleSubmit = step === STEP_CARD_NUMBER ? handleCardNumberSubmit : handleCredentialsSubmit;

  // Go back to step 1
  const handleBack = () => {
    setStep(STEP_CARD_NUMBER);
    setCvv('');
    setAccessCode('');
    setCaptchaToken(null);
    setError(null);
    setFieldErrors({});
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const handleCardNumberChange = e => {
    setCardNumber(e.target.value.trim());
    if (fieldErrors.cardNumber) {
      setFieldErrors(prev => ({ ...prev, cardNumber: null }));
    }
  };

  const handleCvvChange = e => {
    setCvv(e.target.value.trim());
    if (fieldErrors.cvv) {
      setFieldErrors(prev => ({ ...prev, cvv: null }));
    }
  };

  const handleAccessCodeChange = e => {
    const { value } = e.target;
    if (value.length <= 10) {
      setAccessCode(value);
      if (fieldErrors.accessCode) {
        setFieldErrors(prev => ({ ...prev, accessCode: null }));
      }
    }
  };

  const cvvTooltipText = intl.messages[`${page}-cvv-helpertext`];
  const accessCodeTooltipText = intl.messages[`${page}-accesscode-helpertext`];

  // Check if form is valid for current step
  const isFormValid = () => {
    if (step === STEP_CARD_NUMBER) {
      return cardNumber && cardNumber.length === 16;
    }
    return cvv && accessCode && captchaToken;
  };

  // Get logo from theme
  const logoUrl = theme?.brand?.logo_url || theme?.brand?.logo;

  // Get payaccount login URL for "already have account" link.
  // withLang carries the selected language across the domain hop (sessionStorage does not).
  const payAccountLoginUrl = withLang(`${getPayAccountUrl()}/login`, currentLocale);
  // Activation URL for cardholders who don't have an access code.
  // getPayAccountUrl() resolves the env-appropriate base (local/dev/staging/prod).
  const accessCodeActivateUrl = withLang(`${getPayAccountUrl()}/activate`, currentLocale);

  // Check if we should show default decorative elements
  const showDecorations = isCAMSDefaultHost();

  return (
    <PageWrapper>
      {/* Large blob shapes at top corners - only for default hostnames */}
      {showDecorations && (
        <>
          <BlobShape className="green-blob" />
          <BlobShape className="blue-blob" />
        </>
      )}

      {/* Geometric background shapes - only for default hostnames */}
      {showDecorations && (
        <GeometricShapes>
          {/* Top left area - green circles */}
          <Shape className="circle" color="#6EE7B7" style={{ top: '8%', left: '20%', width: '16px', height: '16px' }} opacity={0.6} />
          <Shape className="circle" color="#5C6BC0" style={{ top: '12%', left: '16%', width: '8px', height: '8px' }} opacity={0.7} />

          {/* Top center - hollow and solid squares */}
          <Shape className="hollow-square" color="#6EE7B7" style={{ top: '8%', left: '45%', width: '24px', height: '24px', transform: 'rotate(12deg)' }} opacity={0.8} />
          <Shape className="solid-square" color="#5C6BC0" style={{ top: '6%', left: '48%', width: '32px', height: '32px', transform: 'rotate(-12deg)' }} opacity={0.7} />
          <Shape className="circle" color="#6EE7B7" style={{ top: '14%', left: '52%', width: '8px', height: '8px' }} opacity={0.6} />

          {/* Top right - circles */}
          <Shape className="circle" color="#5C6BC0" style={{ top: '6%', right: '10%', width: '12px', height: '12px' }} opacity={0.8} />
          <Shape className="circle" color="#6EE7B7" style={{ top: '18%', right: '6%', width: '10px', height: '10px' }} opacity={0.7} />
          <Shape className="circle" color="#6EE7B7" style={{ top: '22%', right: '8%', width: '6px', height: '6px' }} opacity={0.7} />

          {/* Left side - hollow squares */}
          <Shape className="hollow-square" color="#5C6BC0" style={{ top: '35%', left: '8%', width: '16px', height: '16px', transform: 'rotate(-12deg)' }} opacity={0.7} />
          <Shape className="hollow-square" color="#6EE7B7" style={{ bottom: '20%', left: '6%', width: '20px', height: '20px', transform: 'rotate(12deg)' }} opacity={0.8} />

          {/* Bottom left - circles */}
          <Shape className="circle" color="#5C6BC0" style={{ bottom: '10%', left: '15%', width: '32px', height: '32px' }} opacity={0.7} />
          <Shape className="circle" color="#6EE7B7" style={{ bottom: '16%', left: '20%', width: '8px', height: '8px' }} opacity={0.6} />

          {/* Bottom right - circles */}
          <Shape className="circle" color="#6EE7B7" style={{ bottom: '12%', right: '12%', width: '12px', height: '12px' }} opacity={0.7} />
          <Shape className="circle" color="#6EE7B7" style={{ bottom: '8%', right: '15%', width: '8px', height: '8px' }} opacity={0.7} />
        </GeometricShapes>
      )}

      {/* Header */}
      <CamsHeader>
        <LogoContainer>
          {logoUrl && <img src={logoUrl} alt="Logo" />}
        </LogoContainer>
        <HeaderNav>
          <HeaderLink
            href="https://help.mycardholdersupport.com/portal/kb/cs"
            target="_blank"
            rel="noopener noreferrer"
          >
            {intl.messages[`${page}-knowledge-base`]}
          </HeaderLink>
          <LanguageToggle onClick={handleLocaleChange}>
            {toggleLocale.toUpperCase()}
          </LanguageToggle>
        </HeaderNav>
      </CamsHeader>

      {/* Main content */}
      <MainContent>
        <Wrapper>
          <Title>{intl.messages[`${page}-title`]}</Title>
          <Subtitle>{intl.messages[`${page}-subtitle`]}</Subtitle>

          <FormContainer onSubmit={handleSubmit}>
            {/* Step 1: Card Number */}
            {step === STEP_CARD_NUMBER && (
              <InputWrapper>
                <InputLabel htmlFor="cardNumber">
                  {intl.messages[`${page}-cardnumber-label`]}
                </InputLabel>
                <Input
                  type="text"
                  id="cardNumber"
                  inputMode="numeric"
                  placeholder={intl.messages[`${page}-cardnumber-placeholder`]}
                  value={cardNumber}
                  onChange={handleCardNumberChange}
                  className={fieldErrors.cardNumber ? 'is-invalid' : ''}
                  disabled={loading}
                  autoComplete="cc-number"
                />
                {fieldErrors.cardNumber && (
                  <FieldErrorMessage>{errorMessage(intl, fieldErrors.cardNumber)}</FieldErrorMessage>
                )}
              </InputWrapper>
            )}

            {/* Step 2: CVV and Access Code */}
            {step === STEP_CREDENTIALS && (
              <>
                <InputWrapper>
                  <InputLabel>
                    {intl.messages[`${page}-cardnumber-label`]}
                  </InputLabel>
                  <Input
                    type="text"
                    value={cardNumber}
                    disabled
                    style={{ backgroundColor: '#f5f5f5' }}
                  />
                </InputWrapper>

                <InputRow>
                  <InputWrapper>
                    <InputLabel htmlFor="cvv">
                      {intl.messages[`${page}-cvv-label`]}
                    </InputLabel>
                    <InputWithTooltip>
                      <Input
                        type="password"
                        id="cvv"
                        placeholder={intl.messages[`${page}-cvv-placeholder`]}
                        value={cvv}
                        onChange={handleCvvChange}
                        className={fieldErrors.cvv ? 'is-invalid' : ''}
                        disabled={loading}
                        minLength={3}
                        maxLength={4}
                      />
                      <TooltipContainer>
                        <HelpIconButton type="button" aria-label="CVV help">?</HelpIconButton>
                        <TooltipBubble>{cvvTooltipText}</TooltipBubble>
                      </TooltipContainer>
                    </InputWithTooltip>
                    {fieldErrors.cvv && (
                      <FieldErrorMessage>{errorMessage(intl, fieldErrors.cvv)}</FieldErrorMessage>
                    )}
                  </InputWrapper>

                  <InputWrapper>
                    <InputLabel htmlFor="accessCode">
                      {intl.messages[`${page}-accesscode-label`]}
                    </InputLabel>
                    <InputWithTooltip>
                      <Input
                        type="password"
                        id="accessCode"
                        placeholder={intl.messages[`${page}-accesscode-placeholder`]}
                        value={accessCode}
                        onChange={handleAccessCodeChange}
                        className={fieldErrors.accessCode ? 'is-invalid' : ''}
                        disabled={loading}
                        autoComplete="off"
                      />
                      <TooltipContainer>
                        <HelpIconButton type="button" aria-label="Access code help">?</HelpIconButton>
                        <TooltipBubble>{accessCodeTooltipText}</TooltipBubble>
                      </TooltipContainer>
                    </InputWithTooltip>
                    {fieldErrors.accessCode && (
                      <FieldErrorMessage>{errorMessage(intl, fieldErrors.accessCode)}</FieldErrorMessage>
                    )}
                    <AccessCodeLink href={accessCodeActivateUrl}>
                      {intl.messages[`${page}-accesscode-help-link`]}
                    </AccessCodeLink>
                  </InputWrapper>
                </InputRow>

                {/* reCAPTCHA */}
                <RecaptchaWrapper style={{ margin: '15px 0' }}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={handleCaptchaChange}
                    onExpired={handleCaptchaExpire}
                    onErrored={handleCaptchaError}
                  />
                </RecaptchaWrapper>
                {fieldErrors.captcha && (
                  <FieldErrorMessage style={{ textAlign: 'center' }}>{errorMessage(intl, fieldErrors.captcha)}</FieldErrorMessage>
                )}
              </>
            )}

            <Button
              type="submit"
              disabled={loading || !isFormValid()}
              className={loading ? 'is-loading' : ''}
            >
              {loading ? (
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    opacity=".2"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12 19a7 7 0 1 0 0-14 7 7 0 0 0 0 14m0 3c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10"
                    fill="currentColor"
                  />
                  <path
                    d="M12 22c5.523 0 10-4.477 10-10h-3a7 7 0 0 1-7 7zM2 12C2 6.477 6.477 2 12 2v3a7 7 0 0 0-7 7z"
                    fill="currentColor"
                  />
                </svg>
              ) : (
                intl.messages[`${page}-submit_title`]
              )}
            </Button>

            {/* Back button for step 2 */}
            {step === STEP_CREDENTIALS && (
              <Button
                type="button"
                onClick={handleBack}
                disabled={loading}
                style={{ marginTop: '10px', backgroundColor: '#6c757d' }}
              >
                {intl.messages[`${page}-back-button`] || 'Back'}
              </Button>
            )}

            {redirectMessage && (
              <SuccessMessage>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="12" r="10" stroke="#155724" strokeWidth="2" />
                  <path d="M8 12l2.5 2.5L16 9" stroke="#155724" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>{redirectMessage}</span>
              </SuccessMessage>
            )}

            {error && (
              <ErrorMessage>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="12" r="10" stroke="#721c24" strokeWidth="2" />
                  <line
                    x1="12"
                    y1="8"
                    x2="12"
                    y2="12"
                    stroke="#721c24"
                    strokeWidth="2"
                  />
                  <circle cx="12" cy="16" r="1" fill="#721c24" />
                </svg>
                <span>{errorMessage(intl, error)}</span>
              </ErrorMessage>
            )}

            <LoginLink href={payAccountLoginUrl}>
              {intl.messages[`${page}-already-have-account`]}
            </LoginLink>
          </FormContainer>
        </Wrapper>

        {/* Footer */}
        <CamsPageFooter theme={theme} locale={locale} />
      </MainContent>
    </PageWrapper>
  );
};

CamsLogin.propTypes = {
  theme: PropTypes.object,
  locale: PropTypes.string,
  setLocale: PropTypes.func,
};

export default withTheme(CamsLogin);